import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { handleUppercaseInput } from "@/lib/utils";
import { API, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { statusConfig, issuePriorityConfig, issueStatusConfig, issueCategoryConfig } from "@/lib/uiConfig";
import DTCManagementSection from "@/components/DTCManagementSection";
import WorkHoldManager from "@/components/WorkHoldManager";
import TimeTracker from "@/components/TimeTracker";
import JobCardStatusWorkflow from "@/components/JobCardStatusWorkflow";
import AddVehicleDataForm from "@/components/forms/AddVehicleDataForm";
import SimpleAddPartForm from "@/components/forms/SimpleAddPartForm";
import VehicleCascadeDropdowns from "@/components/VehicleCascadeDropdowns";
import {
  Plus, Car, Wrench, AlertTriangle, CheckCircle, Clock, Package,
  Search, Bell, Settings, Users, Calendar, FileText, BarChart3,
  Home, Archive, DollarSign, Phone, Mail, MapPin, Menu, X,
  AlertCircle, Bug, Zap, Settings2, Gauge, Cog, Calculator,
  Eye, Download, Camera, Edit, Trash, RefreshCw, LogOut, LogIn,
  UserPlus, Shield, PackageX, Trash2, Play, List, Clipboard, Database, Minus,
  Check, CreditCard, Truck, Send, Code, FileCheck, Activity,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, Loader2,
  Filter, Info, ClipboardList, ChevronDown
} from "lucide-react";


const AddAppointmentForm = ({ customers, vehicles, employees, onAppointmentAdded, onCancel }) => {
  const [loading, setLoading] = useState(false);

  // Cascading dropdown state for vehicle information
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSeries, setSelectedSeries] = useState('');

  // Vehicle data from curated database
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      customer_id: '',
      customer_name: '',
      customer_contact: '',
      customer_email: '',
      vehicle_make: '',
      vehicle_model: '',
      vehicle_year: '',
      vehicle_series: '',
      service_type: '',
      appointment_date: new Date().toISOString().split('T')[0],
      appointment_time: '10:00',
      duration: '120',
      priority: 'medium',
      assigned_technician: '',
      estimated_cost: '',
      notes: ''
    }
  });

  // Fetch vehicle makes from curated database
  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(m => m && m.trim() !== '');
        setAvailableMakes(makes);
      } catch (error) {
        console.error('Error fetching vehicle makes:', error);
        setAvailableMakes([]);
      }
    };
    fetchMakes();
  }, []);

  // Fetch models when make changes
  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedMake) {
        setAvailableModels([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(selectedMake)}`);
        const models = (response.data.models || []).filter(m => m && m.trim() !== '');
        setAvailableModels(models);
      } catch (error) {
        console.error('Error fetching vehicle models:', error);
        setAvailableModels([]);
      }
    };
    fetchModels();
  }, [selectedMake]);

  // Fetch series when make+model changes
  useEffect(() => {
    const fetchSeries = async () => {
      if (!selectedMake || !selectedModel) {
        setAvailableSeries([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(selectedMake)}/${encodeURIComponent(selectedModel)}`);
        const series = (response.data.series || []).filter(s => s && s.trim() !== '');
        setAvailableSeries(series);
      } catch (error) {
        console.error('Error fetching vehicle series:', error);
        setAvailableSeries([]);
      }
    };
    fetchSeries();
  }, [selectedMake, selectedModel]);

  const watchCustomer = watch('customer_id');

  // Filter vehicles based on selected customer (show all vehicles for now)
  const filteredVehicles = vehicles; // Show all vehicles - customer relationship is optional

  const onSubmit = (data) => {
    setLoading(true);

    try {
      // Validate required fields
      if (!data.customer_id || !data.service_type) {
        alert('Please fill all required customer and service fields');
        return;
      }

      // For manual entry, validate customer fields
      if (data.customer_id === 'manual_entry') {
        if (!data.customer_name || !data.customer_contact) {
          alert('Please fill all required customer details (Name and Contact)');
          return;
        }
      }

      // Use cascading dropdown values for vehicle validation
      if (!selectedMake || !selectedModel || !data.vehicle_year) {
        alert('Please fill all required vehicle details (MAKE, MODEL, Year)');
        return;
      }

      const customer = customers.find(c => c.id === data.customer_id);

      // Map frontend service type to backend enum values
      const serviceTypeMapping = {
        "General Service": "regular_service",
        "Oil Change": "oil_change",  
        "Brake Service": "brake_service",
        "AC Repair": "ac_service",
        "Engine Diagnostic": "engine_repair",
        "Transmission Service": "transmission_service",
        "Electrical Repair": "electrical_repair",
        "Body Work": "body_work",
        "Tire Service": "tire_service",
        "Other": "regular_service"
      };

      const appointmentData = {
        customer_id: data.customer_id === 'manual_entry' ? null : data.customer_id,
        customer_name: data.customer_id === 'manual_entry' ? data.customer_name : (customer?.name || ''),
        customer_contact: data.customer_id === 'manual_entry' ? data.customer_contact : (customer?.contact || customer?.phone || ''),
        customer_email: data.customer_id === 'manual_entry' ? data.customer_email : (customer?.email || ''),
        vehicle_id: null, // No vehicle_id since we removed vehicle selection
        vehicle_make: selectedMake.trim(),
        vehicle_model: selectedModel.trim(),
        vehicle_series: selectedSeries.trim() || null,
        vehicle_year: parseInt(data.vehicle_year) || null,
        vehicle_reg: data.license_plate || '',
        vehicle_vin: '',
        appointment_date: data.appointment_date,
        appointment_time: data.appointment_time,
        estimated_duration: parseInt(data.duration) || 60,
        service_type: serviceTypeMapping[data.service_type] || "regular_service",
        service_description: `${data.service_type.replace('_', ' ')} service`,
        priority: data.priority,
        assigned_technician: data.assigned_technician?.trim() || null,
        estimated_cost: parseFloat(data.estimated_cost) || null,
        notes: data.notes?.trim() || null
      };

      onAppointmentAdded(appointmentData);
    } catch (error) {
      console.error('Error creating appointment:', error);
      
      // Show more detailed error message
      let errorMessage = 'An error occurred while scheduling the appointment.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Customer and Vehicle Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Customer & Vehicle Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="customer_id">Customer *</Label>
              <div className="space-y-2">
                <select
                  id="customer_id"
                  {...register("customer_id", { required: "Customer is required" })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  onChange={(e) => {
                    const value = e.target.value;
                    setValue("customer_id", value);
                    if (value === "manual_entry") {
                      setValue("customer_name", "");
                      setValue("customer_contact", "");
                      setValue("customer_email", "");
                    } else if (value) {
                      const customer = customers.find(c => c.id === value);
                      if (customer) {
                        setValue("customer_name", customer.name);
                        setValue("customer_contact", customer.contact || customer.phone);
                        setValue("customer_email", customer.email || "");
                      }
                    }
                  }}
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.contact || customer.phone})
                    </option>
                  ))}
                  <option value="manual_entry">➕ Add New Customer</option>
                </select>
                
                {/* Manual Entry Fields - shown when "Add New Customer" is selected */}
                {watchCustomer === "manual_entry" && (
                  <div className="border border-gray-200 rounded-md p-3 bg-blue-50 space-y-3">
                    <p className="text-sm font-medium text-blue-800 mb-2">Enter New Customer Details:</p>
                    <div>
                      <Label htmlFor="customer_name">Customer Name *</Label>
                      <Input
                        id="customer_name"
                        {...register("customer_name", { required: "Customer name is required" })}
                        placeholder="Enter customer name"
                        className="bg-white"
                      />
                      {errors.customer_name && (
                        <p className="text-sm text-red-600 mt-1">{errors.customer_name.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="customer_contact">Contact Number *</Label>
                      <Input
                        id="customer_contact"
                        {...register("customer_contact", { required: "Contact number is required" })}
                        placeholder="Enter contact number"
                        className="bg-white"
                      />
                      {errors.customer_contact && (
                        <p className="text-sm text-red-600 mt-1">{errors.customer_contact.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="customer_email">Email (Optional)</Label>
                      <Input
                        id="customer_email"
                        type="email"
                        {...register("customer_email")}
                        placeholder="Enter email address"
                        className="bg-white"
                      />
                    </div>
                  </div>
                )}
              </div>
              {errors.customer_id && (
                <p className="text-sm text-red-600 mt-1">{errors.customer_id.message}</p>
              )}
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="bg-gray-50 p-4 rounded-lg border">
            <Label className="text-base font-medium text-gray-900 mb-3 block">Vehicle Details *</Label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="appt_vehicle_make">MAKE *</Label>
                <Select
                  value={selectedMake}
                  onValueChange={(value) => {
                    setSelectedMake(value);
                    setSelectedModel('');
                    setSelectedSeries('');
                    setValue('vehicle_make', value);
                    setValue('vehicle_model', '');
                    setValue('vehicle_series', '');
                  }}
                >
                  <SelectTrigger data-testid="appt-vehicle-make">
                    <SelectValue placeholder="Select Make" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMakes.map((make) => (
                      <SelectItem key={make} value={make}>{make}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.vehicle_make && (
                  <p className="text-sm text-red-600 mt-1">{errors.vehicle_make.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="appt_vehicle_model">MODEL *</Label>
                <Select
                  value={selectedModel}
                  onValueChange={(value) => {
                    setSelectedModel(value);
                    setSelectedSeries('');
                    setValue('vehicle_model', value);
                    setValue('vehicle_series', '');
                  }}
                  disabled={!selectedMake}
                >
                  <SelectTrigger data-testid="appt-vehicle-model">
                    <SelectValue placeholder={selectedMake ? "Select Model" : "Select Make first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableModels.map((model) => (
                      <SelectItem key={model} value={model}>{model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.vehicle_model && (
                  <p className="text-sm text-red-600 mt-1">{errors.vehicle_model.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="appt_vehicle_year">Year *</Label>
                <Input
                  id="appt_vehicle_year"
                  type="number"
                  min="1980"
                  max="2030"
                  {...register("vehicle_year", { required: "Vehicle year is required" })}
                  placeholder="e.g., 2020"
                />
                {errors.vehicle_year && (
                  <p className="text-sm text-red-600 mt-1">{errors.vehicle_year.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="appt_vehicle_series">SERIES</Label>
                <Select
                  value={selectedSeries}
                  onValueChange={(value) => {
                    setSelectedSeries(value);
                    setValue('vehicle_series', value);
                  }}
                  disabled={!selectedMake || !selectedModel}
                >
                  <SelectTrigger data-testid="appt-vehicle-series">
                    <SelectValue placeholder={selectedModel ? "Select Series" : "Select Model first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSeries.map((series) => (
                      <SelectItem key={series} value={series}>{series}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Details */}
      <Card>
        <CardHeader>
          <CardTitle>Service Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="service_type">Service Type *</Label>
              <select
                id="service_type"
                {...register("service_type", { required: "Service type is required" })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select Service Type</option>
                <option value="General Service">General Service</option>
                <option value="Oil Change">Oil Change</option>
                <option value="Brake Service">Brake Service</option>
                <option value="AC Repair">AC Repair</option>
                <option value="Engine Diagnostic">Engine Diagnostic</option>
                <option value="Transmission Service">Transmission Service</option>
                <option value="Electrical Repair">Electrical Repair</option>
                <option value="Body Work">Body Work</option>
                <option value="Tire Service">Tire Service</option>
                <option value="Other">Other</option>
              </select>
              {errors.service_type && (
                <p className="text-sm text-red-600 mt-1">{errors.service_type.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <select
                id="priority"
                {...register("priority")}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduling */}
      <Card>
        <CardHeader>
          <CardTitle>Scheduling</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="appointment_date">Date *</Label>
              <Input
                id="appointment_date"
                type="date"
                {...register("appointment_date", { required: "Date is required" })}
              />
              {errors.appointment_date && (
                <p className="text-sm text-red-600 mt-1">{errors.appointment_date.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="appointment_time">Time *</Label>
              <Input
                id="appointment_time"
                type="time"
                {...register("appointment_time", { required: "Time is required" })}
              />
              {errors.appointment_time && (
                <p className="text-sm text-red-600 mt-1">{errors.appointment_time.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                min="30"
                step="30"
                {...register("duration")}
                placeholder="120"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="assigned_technician">Assigned Technician</Label>
              <select
                id="assigned_technician"
                {...register("assigned_technician")}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select Technician</option>
                {employees
                  .filter(employee => 
                    employee.status === 'active' && 
                    (employee.department === 'service' || 
                     employee.department === 'technical' || 
                     employee.role === 'technician' || 
                     employee.position?.toLowerCase().includes('technician') ||
                     employee.position?.toLowerCase().includes('mechanic') ||
                     employee.position?.toLowerCase().includes('advisor'))
                  )
                  .map(employee => (
                    <option key={employee.id} value={employee.name}>
                      {employee.name} - {employee.position || employee.role}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <Label htmlFor="estimated_cost">Estimated Cost (₹)</Label>
              <Input
                id="estimated_cost"
                type="number"
                step="0.01"
                min="0"
                {...register("estimated_cost")}
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              placeholder="Any additional notes or special instructions"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <Button 
          type="submit" 
          disabled={loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          {loading ? 'Scheduling...' : 'Schedule Appointment'}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

// View Appointment Details Component

const ViewAppointmentDetails = ({ appointment, onClose, onStatusChange }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-purple-100 text-purple-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-blue-900">{appointment.appointment_number}</h3>
            <p className="text-sm text-blue-700">
              {new Date(appointment.appointment_date).toLocaleDateString()} at {appointment.appointment_time}
            </p>
          </div>
          <div className="flex gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
              {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1).replace('_', ' ')}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(appointment.priority)}`}>
              {appointment.priority.charAt(0).toUpperCase() + appointment.priority.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Customer Information */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-600">Name</p>
              <p className="text-sm text-gray-900">{appointment.customer_name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Phone</p>
              <p className="text-sm text-gray-900">{appointment.customer_phone}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Information */}
      <Card>
        <CardHeader>
          <CardTitle>Vehicle Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-600">Make</p>
              <p className="text-sm text-gray-900">{appointment.vehicle_make || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Model</p>
              <p className="text-sm text-gray-900">{appointment.vehicle_model || 'Not specified'}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-600">Year</p>
              <p className="text-sm text-gray-900">{appointment.vehicle_year || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Series</p>
              <p className="text-sm text-gray-900">{appointment.vehicle_series || 'Not specified'}</p>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Full Description</p>
            <p className="text-sm text-gray-900 font-medium">{appointment.vehicle_info}</p>
          </div>
        </CardContent>
      </Card>

      {/* Service Details */}
      <Card>
        <CardHeader>
          <CardTitle>Service Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-600">Service Type</p>
              <p className="text-sm text-gray-900">{appointment.service_type}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Duration</p>
              <p className="text-sm text-gray-900">{appointment.duration} minutes</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-600">Assigned Technician</p>
              <p className="text-sm text-gray-900">{appointment.assigned_technician || 'Not assigned'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Estimated Cost</p>
              <p className="text-sm text-gray-900">{formatCurrency(appointment.estimated_cost)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {appointment.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{appointment.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Status Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {appointment.status === 'scheduled' && (
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => onStatusChange(appointment.id, 'confirmed')}
              >
                <CheckCircle className="h-3 w-3 mr-1" />
                Confirm Appointment
              </Button>
            )}
            {appointment.status === 'confirmed' && (
              <Button
                size="sm"
                className="bg-yellow-600 hover:bg-yellow-700"
                onClick={() => onStatusChange(appointment.id, 'in_progress')}
              >
                <Play className="h-3 w-3 mr-1" />
                Start Service
              </Button>
            )}
            {appointment.status === 'in_progress' && (
              <Button
                size="sm"
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => onStatusChange(appointment.id, 'completed')}
              >
                <CheckCircle className="h-3 w-3 mr-1" />
                Mark Complete
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 border-red-600 hover:bg-red-50"
              onClick={() => onStatusChange(appointment.id, 'cancelled')}
            >
              <X className="h-3 w-3 mr-1" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Close Button */}
      <div className="flex justify-end">
        <Button onClick={onClose} variant="outline">
          Close
        </Button>
      </div>
    </div>
  );
};

// Edit Appointment Form Component

const EditAppointmentForm = ({ appointment, customers, vehicles, employees, onAppointmentUpdated, onCancel }) => {
  const [loading, setLoading] = useState(false);

  // Cascade vehicle dropdown states
  const [selectedMake, setSelectedMake] = useState(appointment.vehicle_make || '');
  const [selectedModel, setSelectedModel] = useState(appointment.vehicle_model || '');
  const [selectedSeries, setSelectedSeries] = useState(appointment.vehicle_series || '');
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      customer_id: appointment.customer_id || '',
      vehicle_id: appointment.vehicle_id || '',
      vehicle_make: appointment.vehicle_make || '',
      vehicle_model: appointment.vehicle_model || '',
      vehicle_year: appointment.vehicle_year || '',
      vehicle_series: appointment.vehicle_series || '',
      service_type: appointment.service_type || 'regular_service',
      appointment_date: appointment.appointment_date || '',
      appointment_time: appointment.appointment_time || '',
      duration: appointment.duration?.toString() || '120',
      priority: appointment.priority || 'medium',
      assigned_technician: appointment.assigned_technician || '',
      estimated_cost: appointment.estimated_cost?.toString() || '',
      notes: appointment.notes || ''
    }
  });

  // Fetch vehicle makes from curated database
  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(m => m && m.trim() !== '');
        setAvailableMakes(makes);
      } catch (error) {
        console.error('Error fetching vehicle makes:', error);
      }
    };
    fetchMakes();
  }, []);

  // Fetch models when make changes
  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedMake) { setAvailableModels([]); return; }
      try {
        const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(selectedMake)}`);
        const models = (response.data.models || []).filter(m => m && m.trim() !== '');
        setAvailableModels(models);
      } catch (error) {
        console.error('Error fetching vehicle models:', error);
        setAvailableModels([]);
      }
    };
    fetchModels();
  }, [selectedMake]);

  // Fetch series when make+model changes
  useEffect(() => {
    const fetchSeries = async () => {
      if (!selectedMake || !selectedModel) { setAvailableSeries([]); return; }
      try {
        const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(selectedMake)}/${encodeURIComponent(selectedModel)}`);
        const series = (response.data.series || []).filter(s => s && s.trim() !== '');
        setAvailableSeries(series);
      } catch (error) {
        console.error('Error fetching vehicle series:', error);
        setAvailableSeries([]);
      }
    };
    fetchSeries();
  }, [selectedMake, selectedModel]);

  const watchCustomer = watch('customer_id');
  const watchVehicle = watch('vehicle_id');

  // Filter vehicles based on selected customer (show all vehicles for now)
  const filteredVehicles = vehicles; // Show all vehicles - customer relationship is optional

  // Auto-fill vehicle details when vehicle is selected from dropdown
  React.useEffect(() => {
    if (watchVehicle && watchVehicle !== appointment.vehicle_id) {
      const selectedVehicle = vehicles.find(v => v.id === watchVehicle);
      if (selectedVehicle) {
        const make = selectedVehicle.make || selectedVehicle.vehicle_make || '';
        const model = selectedVehicle.model || selectedVehicle.vehicle_model || '';
        const series = selectedVehicle.series || selectedVehicle.vehicle_series || '';
        setSelectedMake(make);
        setSelectedModel(model);
        setSelectedSeries(series);
        setValue('vehicle_make', make);
        setValue('vehicle_model', model);
        setValue('vehicle_year', selectedVehicle.year || selectedVehicle.vehicle_year || '');
        setValue('vehicle_series', series);
      }
    }
  }, [watchVehicle, vehicles, setValue, appointment.vehicle_id]);

  const onSubmit = (data) => {
    setLoading(true);

    try {
      if (!data.customer_id || !data.vehicle_id || !data.service_type) {
        alert('Please fill all required fields');
        return;
      }

      if (!selectedMake || !selectedModel || !data.vehicle_year) {
        alert('Please fill all required vehicle details (Make, Model, Year)');
        return;
      }

      const customer = customers.find(c => c.id === data.customer_id);
      const vehicle = vehicles.find(v => v.id === data.vehicle_id);

      // Create comprehensive vehicle info string
      const vehicleInfoString = `${data.vehicle_year} ${selectedMake} ${selectedModel}${selectedSeries ? ` ${selectedSeries}` : ''}`.trim();

      const updatedAppointmentData = {
        ...appointment, // Keep existing fields like id, appointment_number, created_at, status
        customer_id: data.customer_id,
        customer_name: customer?.name || '',
        customer_contact: customer?.contact || customer?.phone || '',
        customer_email: customer?.email || '',
        vehicle_id: data.vehicle_id,
        vehicle_make: selectedMake.trim(),
        vehicle_model: selectedModel.trim(),
        vehicle_series: selectedSeries.trim() || null,
        vehicle_year: parseInt(data.vehicle_year) || null,
        vehicle_reg: data.license_plate || vehicle?.license_plate || '',
        vehicle_vin: vehicle?.vin || '',
        appointment_date: data.appointment_date,
        appointment_time: data.appointment_time,
        estimated_duration: parseInt(data.duration) || 60,
        service_type: data.service_type.trim(),
        service_description: `${data.service_type.replace('_', ' ')} service`,
        priority: data.priority,
        assigned_technician: data.assigned_technician?.trim() || null,
        estimated_cost: parseFloat(data.estimated_cost) || null,
        notes: data.notes?.trim() || null
      };

      onAppointmentUpdated(updatedAppointmentData);
    } catch (error) {
      console.error('Error updating appointment:', error);
      
      // Show more detailed error message
      let errorMessage = 'An error occurred while updating the appointment.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Customer and Vehicle Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Customer & Vehicle Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_id">Customer *</Label>
              <select
                id="customer_id"
                {...register("customer_id", { required: "Customer is required" })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select Customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} ({customer.contact || customer.phone})
                  </option>
                ))}
              </select>
              {errors.customer_id && (
                <p className="text-sm text-red-600 mt-1">{errors.customer_id.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="vehicle_id">Select Vehicle *</Label>
              <select
                id="vehicle_id"
                {...register("vehicle_id", { required: "Vehicle is required" })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select Vehicle</option>
                {filteredVehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.make && vehicle.model && vehicle.year 
                      ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` 
                      : vehicle.info || vehicle.vehicle_info || 'Unknown Vehicle'} ({vehicle.license_plate || vehicle.license || 'No License'})
                  </option>
                ))}
              </select>
              {errors.vehicle_id && (
                <p className="text-sm text-red-600 mt-1">{errors.vehicle_id.message}</p>
              )}
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="bg-gray-50 p-4 rounded-lg border">
            <Label className="text-base font-medium text-gray-900 mb-3 block">Vehicle Details *</Label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="edit_appt_vehicle_make">MAKE *</Label>
                <Select
                  value={selectedMake}
                  onValueChange={(value) => {
                    setSelectedMake(value);
                    setSelectedModel('');
                    setSelectedSeries('');
                    setValue('vehicle_make', value);
                    setValue('vehicle_model', '');
                    setValue('vehicle_series', '');
                  }}
                >
                  <SelectTrigger data-testid="edit-appt-vehicle-make">
                    <SelectValue placeholder="Select Make" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMakes.map((make) => (
                      <SelectItem key={make} value={make}>{make}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.vehicle_make && (
                  <p className="text-sm text-red-600 mt-1">{errors.vehicle_make.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="edit_appt_vehicle_model">MODEL *</Label>
                <Select
                  value={selectedModel}
                  onValueChange={(value) => {
                    setSelectedModel(value);
                    setSelectedSeries('');
                    setValue('vehicle_model', value);
                    setValue('vehicle_series', '');
                  }}
                  disabled={!selectedMake}
                >
                  <SelectTrigger data-testid="edit-appt-vehicle-model">
                    <SelectValue placeholder={selectedMake ? "Select Model" : "Select Make first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableModels.map((model) => (
                      <SelectItem key={model} value={model}>{model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.vehicle_model && (
                  <p className="text-sm text-red-600 mt-1">{errors.vehicle_model.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="edit_appt_vehicle_year">Year *</Label>
                <Input
                  id="edit_appt_vehicle_year"
                  type="number"
                  min="1980"
                  max="2030"
                  {...register("vehicle_year", { required: "Vehicle year is required" })}
                  placeholder="e.g., 2020"
                />
                {errors.vehicle_year && (
                  <p className="text-sm text-red-600 mt-1">{errors.vehicle_year.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="edit_appt_vehicle_series">SERIES</Label>
                <Select
                  value={selectedSeries}
                  onValueChange={(value) => {
                    setSelectedSeries(value);
                    setValue('vehicle_series', value);
                  }}
                  disabled={!selectedMake || !selectedModel}
                >
                  <SelectTrigger data-testid="edit-appt-vehicle-series">
                    <SelectValue placeholder={selectedModel ? "Select Series" : "Select Model first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSeries.map((series) => (
                      <SelectItem key={series} value={series}>{series}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Details */}
      <Card>
        <CardHeader>
          <CardTitle>Service Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="service_type">Service Type *</Label>
              <select
                id="service_type"
                {...register("service_type", { required: "Service type is required" })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select Service Type</option>
                <option value="General Service">General Service</option>
                <option value="Oil Change">Oil Change</option>
                <option value="Brake Service">Brake Service</option>
                <option value="AC Repair">AC Repair</option>
                <option value="Engine Diagnostic">Engine Diagnostic</option>
                <option value="Transmission Service">Transmission Service</option>
                <option value="Electrical Repair">Electrical Repair</option>
                <option value="Body Work">Body Work</option>
                <option value="Tire Service">Tire Service</option>
                <option value="Other">Other</option>
              </select>
              {errors.service_type && (
                <p className="text-sm text-red-600 mt-1">{errors.service_type.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <select
                id="priority"
                {...register("priority")}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduling */}
      <Card>
        <CardHeader>
          <CardTitle>Scheduling</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="appointment_date">Date *</Label>
              <Input
                id="appointment_date"
                type="date"
                {...register("appointment_date", { required: "Date is required" })}
              />
              {errors.appointment_date && (
                <p className="text-sm text-red-600 mt-1">{errors.appointment_date.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="appointment_time">Time *</Label>
              <Input
                id="appointment_time"
                type="time"
                {...register("appointment_time", { required: "Time is required" })}
              />
              {errors.appointment_time && (
                <p className="text-sm text-red-600 mt-1">{errors.appointment_time.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                min="30"
                step="30"
                {...register("duration")}
                placeholder="120"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="assigned_technician">Assigned Technician</Label>
              <select
                id="assigned_technician"
                {...register("assigned_technician")}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select Technician</option>
                {employees
                  .filter(employee => 
                    employee.status === 'active' && 
                    (employee.department === 'service' || 
                     employee.department === 'technical' || 
                     employee.role === 'technician' || 
                     employee.position?.toLowerCase().includes('technician') ||
                     employee.position?.toLowerCase().includes('mechanic') ||
                     employee.position?.toLowerCase().includes('advisor'))
                  )
                  .map(employee => (
                    <option key={employee.id} value={employee.name}>
                      {employee.name} - {employee.position || employee.role}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <Label htmlFor="estimated_cost">Estimated Cost (₹)</Label>
              <Input
                id="estimated_cost"
                type="number"
                step="0.01"
                min="0"
                {...register("estimated_cost")}
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              placeholder="Any additional notes or special instructions"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <Button 
          type="submit" 
          disabled={loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          {loading ? 'Updating...' : 'Update Appointment'}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Appointments Section Component

const AppointmentsSection = ({ onUpdate }) => {
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Sample appointments data for initial setup - will be replaced by real appointments API later
  const sampleAppointments = [];

  // Sample customers and vehicles for dropdowns - REMOVE THIS AND USE REAL DATA
  // These will be replaced by API calls below

  useEffect(() => {
    // Load appointments and real customer/vehicle data
    const loadAppointments = async () => {
      setLoading(true);
      try {
        // Fetch real customers and vehicles from API
        await Promise.all([
          fetchCustomersData(),
          fetchVehiclesData(),
          fetchEmployeesData(),
          loadAppointmentsData()
        ]);
      } catch (error) {
        console.error('Error loading appointment data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAppointments();
  }, []);

  // Fetch real customers from API
  const fetchCustomersData = async () => {
    try {
      console.log('Fetching customers from:', `${API}/customers`);
      const response = await axios.get(`${API}/customers`);
      console.log('Customers API response:', response.data);
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      console.error('Failed API URL:', `${API}/customers`);
      // Fallback to empty array if API fails
      setCustomers([]);
    }
  };

  // Fetch real vehicles from API
  const fetchVehiclesData = async () => {
    try {
      console.log('Fetching vehicles from:', `${API}/vehicles`);
      const response = await axios.get(`${API}/vehicles`);
      console.log('Vehicles API response:', response.data);
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      console.error('Failed API URL:', `${API}/vehicles`);
      // Fallback to empty array if API fails
      setVehicles([]);
    }
  };

  // Fetch real employees from API
  const fetchEmployeesData = async () => {
    try {
      console.log('Fetching employees from:', `${API}/employees`);
      const response = await axios.get(`${API}/employees`);
      console.log('Employees API response:', response.data);
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees from API:', error);
      console.error('Failed API URL:', `${API}/employees`);
      
      // Fallback to localStorage if API fails
      try {
        const storedEmployees = localStorage.getItem('garage_employees');
        if (storedEmployees) {
          console.log('Using localStorage fallback for employees');
          const employeeData = JSON.parse(storedEmployees);
          setEmployees(employeeData || []);
        } else {
          // If no stored employees and API fails, initialize with sample data
          console.log('Initializing with sample employees data');
          const sampleEmployees = [
            {
              id: "emp-001",
              employee_id: "EMP001",
              name: "Rajesh Kumar",
              position: "Senior Mechanic",
              department: "service",
              contact: "+91-9876543210",
              email: "rajesh.kumar@garage.com",
              address: "123 Tech Street, Mumbai",
              salary: 45000,
              date_joined: "2023-01-15",
              status: "active"
            },
            {
              id: "emp-002", 
              employee_id: "EMP002",
              name: "Priya Sharma",
              position: "Service Advisor", 
              department: "service",
              contact: "+91-9123456789",
              email: "priya.sharma@garage.com",
              address: "456 Service Lane, Mumbai",
              salary: 35000,
              date_joined: "2023-03-01",
              status: "active"
            }
          ];
          setEmployees(sampleEmployees);
          localStorage.setItem('garage_employees', JSON.stringify(sampleEmployees));
        }
      } catch (localError) {
        console.error('Error with localStorage fallback:', localError);
        setEmployees([]);
      }
    }
  };

  // Load appointments data
  const loadAppointmentsData = async () => {
    try {
      // Fetch appointments from the backend API
      console.log('Fetching appointments from:', `${API}/appointments`);
      const response = await axios.get(`${API}/appointments`);
      console.log('Appointments loaded from backend:', response.data);
      setAppointments(response.data || []);
      
      // Also save to localStorage as backup
      if (response.data && response.data.length > 0) {
        localStorage.setItem('garage_appointments', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error loading appointments from backend:', error);
      console.error('Failed API URL:', `${API}/appointments`);
      
      // Fallback to localStorage if backend fails
      try {
        const storedAppointments = localStorage.getItem('garage_appointments');
        if (storedAppointments) {
          console.log('Using localStorage fallback for appointments');
          setAppointments(JSON.parse(storedAppointments));
        } else {
          // If no stored appointments and backend fails, use empty array
          setAppointments([]);
        }
      } catch (localError) {
        console.error('Error loading appointments from localStorage:', localError);
        setAppointments([]);
      }
    }
  };

  // Save appointments to localStorage whenever appointments change
  useEffect(() => {
    if (appointments.length > 0) {
      localStorage.setItem('garage_appointments', JSON.stringify(appointments));
    }
  }, [appointments]);

  // Handle adding new appointment
  const handleAppointmentAdded = async (newAppointment) => {
    try {
      console.log('Creating appointment with data:', newAppointment);
      // Send the appointment to the backend API
      const response = await axios.post(`${API}/appointments`, newAppointment);
      
      // Add the created appointment to the local state
      const createdAppointment = response.data;
      console.log('Appointment created successfully:', createdAppointment);
      setAppointments(prevAppointments => [...prevAppointments, createdAppointment]);
      
      // Update localStorage
      const updatedAppointments = [...appointments, createdAppointment];
      localStorage.setItem('garage_appointments', JSON.stringify(updatedAppointments));
      
      setShowAddDialog(false);
      
      // Show success message
      toast.success(`Appointment ${createdAppointment.appointment_number} scheduled successfully!`, {
        description: `Customer: ${createdAppointment.customer_name} | ${createdAppointment.appointment_date} at ${createdAppointment.appointment_time}`
      });
      
      // Refresh data from backend to ensure consistency
      setTimeout(() => {
        loadAppointmentsData();
      }, 1000);
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error creating appointment:', error);
      console.error('Failed API URL:', `${API}/appointments`);
      
      // Fallback to localStorage method if backend fails
      const appointmentWithId = {
        ...newAppointment,
        id: `apt-${Date.now()}`,
        appointment_number: `APT-2025-${String(appointments.length + 5).padStart(3, '0')}`,
        created_at: new Date().toISOString(),
        created_by: 'admin',
        status: 'scheduled'
      };
      
      setAppointments([...appointments, appointmentWithId]);
      setShowAddDialog(false);
      
      // Show fallback success message
      toast.success(`Appointment ${appointmentWithId.appointment_number} scheduled successfully!`, {
        description: `Customer: ${appointmentWithId.customer_name} | ${appointmentWithId.appointment_date} at ${appointmentWithId.appointment_time}`
      });
      
      if (onUpdate) onUpdate();
    }
  };

  // Handle viewing appointment
  const handleViewAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowViewDialog(true);
  };

  // Handle editing appointment
  const handleEditAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowEditDialog(true);
  };

  // Handle appointment updated
  const handleAppointmentUpdated = async (updatedAppointment) => {
    try {
      console.log('Updating appointment with data:', updatedAppointment);
      // Send update to backend API
      const response = await axios.put(
        `${API}/appointments/${updatedAppointment.id}`,
        updatedAppointment
      );
      
      // Update local state with response data
      const serverUpdatedAppointment = response.data;
      console.log('Appointment updated successfully:', serverUpdatedAppointment);
      setAppointments(appointments.map(a => 
        a.id === updatedAppointment.id ? serverUpdatedAppointment : a
      ));
      
      // Refresh data from backend to ensure consistency
      setTimeout(() => {
        loadAppointmentsData();
      }, 1000);
      
    } catch (error) {
      console.error('Error updating appointment:', error);
      console.error('Failed API URL:', `${API}/appointments/${updatedAppointment.id}`);
      
      // Fallback to local update if backend fails
      setAppointments(appointments.map(a => 
        a.id === updatedAppointment.id ? { ...updatedAppointment } : a
      ));
    }
    
    setShowEditDialog(false);
    setSelectedAppointment(null);
    if (onUpdate) onUpdate();
  };

  const handleDeleteAppointment = async (appointment) => {
    if (!window.confirm(`Are you sure you want to delete appointment ${appointment.appointment_number}?`)) return;
    
    try {
      await axios.delete(`${API}/appointments/${appointment.id}`);
      setAppointments(appointments.filter(a => a.id !== appointment.id));
      toast.success("Appointment deleted successfully!");
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error deleting appointment:", error);
      toast.error("Failed to delete appointment");
    }
  };

  // Handle status change
  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      console.log('Updating appointment status:', appointmentId, 'to', newStatus);
      // Send status update to backend API
      const response = await axios.put(
        `${API}/appointments/${appointmentId}`,
        { status: newStatus }
      );
      
      // Update local state with response data
      const updatedAppointment = response.data;
      console.log('Appointment status updated successfully:', updatedAppointment);
      setAppointments(appointments.map(a => 
        a.id === appointmentId ? updatedAppointment : a
      ));
      
    } catch (error) {
      console.error('Error updating appointment status:', error);
      console.error('Failed API URL:', `${API}/appointments/${appointmentId}`);
      
      // Fallback to local update if backend fails
      setAppointments(appointments.map(a => 
        a.id === appointmentId ? { ...a, status: newStatus } : a
      ));
    }
    
    if (onUpdate) onUpdate();
  };

  // Filter appointments
  const filteredAppointments = appointments.filter(appointment => {
    const matchesStatus = statusFilter === 'all' || appointment.status === statusFilter;
    const matchesSearch = searchTerm === '' || 
      appointment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.vehicle_make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.vehicle_model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.service_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.appointment_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Remove the restrictive date filter - show all appointments in list view
    // Date filter only applies to calendar view if implemented
    const matchesToday = viewMode === 'calendar' ? 
      new Date(appointment.appointment_date).toDateString() === new Date().toDateString() : true;
    
    return matchesStatus && matchesSearch && matchesToday;
  });

  // Get appointment statistics
  const getStats = () => {
    const today = new Date().toDateString();
    return {
      total: appointments.length,
      today: appointments.filter(a => new Date(a.appointment_date).toDateString() === today).length,
      scheduled: appointments.filter(a => a.status === 'scheduled').length,
      confirmed: appointments.filter(a => a.status === 'confirmed').length,
      inProgress: appointments.filter(a => a.status === 'in_progress').length,
      completed: appointments.filter(a => a.status === 'completed').length,
      cancelled: appointments.filter(a => a.status === 'cancelled').length
    };
  };

  const stats = getStats();

  // Status configuration
  const statusConfig = {
    scheduled: { color: 'bg-blue-100 text-blue-800', label: 'Scheduled' },
    confirmed: { color: 'bg-green-100 text-green-800', label: 'Confirmed' },
    in_progress: { color: 'bg-yellow-100 text-yellow-800', label: 'In Progress' },
    completed: { color: 'bg-purple-100 text-purple-800', label: 'Completed' },
    cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
  };

  // Priority configuration
  const priorityConfig = {
    low: { color: 'bg-gray-100 text-gray-800', label: 'Low' },
    medium: { color: 'bg-yellow-100 text-yellow-800', label: 'Medium' },
    high: { color: 'bg-red-100 text-red-800', label: 'High' },
    urgent: { color: 'bg-red-100 text-red-800', label: 'Urgent' }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Appointments</h1>
          <p className="text-gray-600">Manage customer appointments and scheduling</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => setShowAddDialog(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Schedule Appointment
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm font-medium text-gray-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.today}</p>
                <p className="text-sm font-medium text-gray-500">Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.scheduled}</p>
                <p className="text-sm font-medium text-gray-500">Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.confirmed}</p>
                <p className="text-sm font-medium text-gray-500">Confirmed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Play className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p>
                <p className="text-sm font-medium text-gray-500">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
                <p className="text-sm font-medium text-gray-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <X className="h-8 w-8 text-red-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.cancelled}</p>
                <p className="text-sm font-medium text-gray-500">Cancelled</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Label htmlFor="search">Search:</Label>
                <Input
                  id="search"
                  placeholder="Search appointments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Label htmlFor="status">Status:</Label>
                <select
                  id="status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4 mr-1" />
                List
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('calendar')}
              >
                <Calendar className="h-4 w-4 mr-1" />
                Calendar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appointments List */}
      {viewMode === 'list' && (
        <div className="grid gap-4">
          {filteredAppointments.length > 0 ? (
            filteredAppointments.map((appointment) => (
              <Card key={appointment.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {appointment.appointment_number}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig[appointment.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                          {statusConfig[appointment.status]?.label || appointment.status}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityConfig[appointment.priority]?.color || 'bg-gray-100 text-gray-800'}`}>
                          {priorityConfig[appointment.priority]?.label || appointment.priority}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Customer</p>
                          <p className="text-sm text-gray-900">{appointment.customer_name}</p>
                          <p className="text-xs text-gray-500">{appointment.customer_phone}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Vehicle</p>
                          <p className="text-sm text-gray-900">{appointment.vehicle_info}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Service</p>
                          <p className="text-sm text-gray-900">{appointment.service_type}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Date & Time</p>
                          <p className="text-sm text-gray-900">
                            {new Date(appointment.appointment_date).toLocaleDateString()} at {appointment.appointment_time}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Duration</p>
                          <p className="text-sm text-gray-900">{appointment.duration} minutes</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Technician</p>
                          <p className="text-sm text-gray-900">{appointment.assigned_technician || 'Not assigned'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Estimated Cost</p>
                          <p className="text-sm text-gray-900">{formatCurrency(appointment.estimated_cost)}</p>
                        </div>
                      </div>

                      {appointment.notes && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-500">Notes</p>
                          <p className="text-sm text-gray-700">{appointment.notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="ml-6 flex flex-col space-y-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewAppointment(appointment)}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditAppointment(appointment)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          console.log('Delete appointment button clicked:', appointment);
                          handleDeleteAppointment(appointment);
                        }}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                      
                      {/* Quick status change buttons */}
                      {appointment.status === 'scheduled' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleStatusChange(appointment.id, 'confirmed')}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Confirm
                        </Button>
                      )}
                      {appointment.status === 'confirmed' && (
                        <Button
                          size="sm"
                          className="bg-yellow-600 hover:bg-yellow-700 text-white"
                          onClick={() => handleStatusChange(appointment.id, 'in_progress')}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Start
                        </Button>
                      )}
                      {appointment.status === 'in_progress' && (
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={() => handleStatusChange(appointment.id, 'completed')}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Complete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No appointments found</h3>
              <p className="text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search criteria.' : 'Schedule your first appointment to get started.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Calendar View - Simple implementation */}
      {viewMode === 'calendar' && (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Calendar View</h3>
              <p className="text-sm text-gray-500 mb-4">
                Calendar view with drag-and-drop scheduling coming soon!
              </p>
              <p className="text-sm text-gray-500">
                For now, use the List view to manage your appointments.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setViewMode('list')}
                className="mt-4"
              >
                Switch to List View
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Appointment Modal */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule New Appointment</DialogTitle>
          </DialogHeader>
          <AddAppointmentForm 
            customers={customers}
            vehicles={vehicles}
            employees={employees}
            onAppointmentAdded={handleAppointmentAdded}
            onCancel={() => setShowAddDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* View Appointment Modal */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>View Appointment - {selectedAppointment?.appointment_number}</DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <ViewAppointmentDetails 
              appointment={selectedAppointment}
              onClose={() => setShowViewDialog(false)}
              onStatusChange={handleStatusChange}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Modal */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Appointment - {selectedAppointment?.appointment_number}</DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <EditAppointmentForm 
              appointment={selectedAppointment}
              customers={customers}
              vehicles={vehicles}
              employees={employees}
              onAppointmentUpdated={handleAppointmentUpdated}
              onCancel={() => {
                setShowEditDialog(false);
                setSelectedAppointment(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Quotations Section Component

export { AppointmentsSection };
